import { useEffect } from 'react';

import { OdProtosSellSellerData_SellerExperience as SellerExperience } from '__generated__/globalTypes';
import { useObservability } from 'platform/observability';
import type { SellerTransaction } from 'sellDirect/graphql/useSellerTransaction';

type TransactionWithSellerExperience = Extract<
  SellerTransaction,
  { __typename: 'QualifiedTransaction' | 'InContractTransaction' }
>;

const isTransactionWithSellerExperience = (
  sellerTransaction: SellerTransaction,
): sellerTransaction is TransactionWithSellerExperience =>
  sellerTransaction.__typename === 'QualifiedTransaction' ||
  sellerTransaction.__typename === 'InContractTransaction';

const computeLegacySellerExperience = (
  shouldSeeAgentExperience: boolean,
  isAlo: boolean,
): SellerExperience => {
  if (shouldSeeAgentExperience) return SellerExperience.SELLER_EXPERIENCE_AGENT_ACQUISITION;
  if (isAlo) return SellerExperience.SELLER_EXPERIENCE_AGENT_LED_OFFERS;
  return SellerExperience.SELLER_EXPERIENCE_DIRECT_TO_CONSUMER;
};

type Params = {
  sellerTransaction: SellerTransaction | null;
  /** True while any data that feeds the legacy SOT computation is still loading. */
  loading: boolean;
  isAgentLedOffersExperience: boolean;
  shouldSeeAgentExperience: boolean;
  sellerLeadUuid: string | undefined;
};

/**
 * Shadow-compares sellerTransaction.sellerExperience against legacy computed experience flags.
 * Remove once sellerTransaction is the sole source of truth.
 */
export const useShadowCompareSellerExperience = ({
  sellerTransaction,
  loading,
  isAgentLedOffersExperience: isAlo,
  shouldSeeAgentExperience,
  sellerLeadUuid,
}: Params): void => {
  const { trackError } = useObservability();

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!sellerTransaction || !isTransactionWithSellerExperience(sellerTransaction)) {
      return;
    }
    const legacySOT = computeLegacySellerExperience(shouldSeeAgentExperience, isAlo);

    if (sellerTransaction.sellerExperience !== legacySOT) {
      trackError('seller-experience-shadow-discrepancy', undefined, {
        sellerLeadUuid,
        shouldSeeAgentExperience,
        isAgentLedOffersExperience: isAlo,
        discrepancies: {
          sellerExperience: {
            legacy: legacySOT,
            sellerTransaction: sellerTransaction.sellerExperience,
          },
        },
      });
    }
  }, [sellerTransaction, loading, isAlo, shouldSeeAgentExperience, sellerLeadUuid, trackError]);
};
